'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { useQueryClient } from '@lib/query/client';
import { toast } from 'sonner';
import type { FlowDocumentProcessingRetryResult, FlowDocumentProcessingRetryVariables } from '../types/flow-document-processing-retry-result';
import { applyFlowDocumentProcessingRetryError } from '../utils/apply-flow-document-processing-retry-error';
import { applyFlowDocumentProcessingRetryOutcome } from '../utils/apply-flow-document-processing-retry-outcome';
import { flowQueryKeys } from './flow-query-keys';
import { useRetryFlowDocumentProcessing } from './use-retry-flow-document-processing';
export interface UseFlowDocumentProcessingRetryOptions {
  /** When set, 409 still-processing uses this instead of a toast (builder Coach). */
  onStillProcessing?: () => void;
}

/**
 * Shared client path for POST retry-processing across upload surfaces (builder,
 * quick sign, etc.): typed outcomes, flow detail invalidation, Coach-friendly copy.
 *
 * Wired: V1 `doc-builder`, Quick Sign `doc-signing-builder`, EB create-deal /
 * template bulk import retry (post-accept), plus builder banners via
 * `shouldOfferFlowDocumentProcessingRetry`.
 */
export function useFlowDocumentProcessingRetry(t0) {
  const $ = _c(9);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const options = t1;
  const queryClient = useQueryClient();
  const retryMutation = useRetryFlowDocumentProcessing();
  let t2;
  if ($[2] !== options.onStillProcessing || $[3] !== queryClient || $[4] !== retryMutation) {
    t2 = async input => {
      ;
      try {
        const result = await retryMutation.mutateAsync(input);
        await applyFlowDocumentProcessingRetryOutcome({
          result,
          toastError: _temp,
          toastSuccess: _temp2,
          invalidateFlowDetail: async () => {
            await Promise.all([queryClient.invalidateQueries({
              queryKey: flowQueryKeys.detail(input.flowId)
            }), queryClient.invalidateQueries({
              queryKey: flowQueryKeys.lists()
            })]);
          }
        });
        return result;
      } catch (t3) {
        const error = t3;
        applyFlowDocumentProcessingRetryError({
          error,
          toastError: _temp3,
          onStillProcessing: options.onStillProcessing
        });
        return;
      }
    };
    $[2] = options.onStillProcessing;
    $[3] = queryClient;
    $[4] = retryMutation;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const retryDocumentProcessing = t2;
  let t3;
  if ($[6] !== retryDocumentProcessing || $[7] !== retryMutation.isPending) {
    t3 = {
      retryDocumentProcessing,
      isRetryingDocumentProcessing: retryMutation.isPending
    };
    $[6] = retryDocumentProcessing;
    $[7] = retryMutation.isPending;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function _temp3(message) {
  return toast.error(message);
}
function _temp2(message_1) {
  return toast.success(message_1);
}
function _temp(message_0) {
  return toast.error(message_0);
}